import React, { useEffect, useMemo, useState } from "react";
import { Button, Empty, Input, Select, Space, Table, Tag, Tooltip } from "antd";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import InsideHeader from "../../components/InsideHeader";
import { fetchWhatsAppQueue } from "../../features/whatsapp/whatsappSlice";
import { formatWhatsAppDate, statusColor, truncate } from "./whatsappUtils";
import "./whatsapp.css";

const statusOptions = ["PENDING", "PROCESSING", "SENT", "DELIVERED", "READ", "FAILED"].map((status) => ({
  value: status,
  label: status,
}));

const WhatsAppQueue = () => {
  const dispatch = useDispatch();
  const { queueData, queueCount, loadingQueue } = useSelector(
    (state) => state.whatsapp
  );
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const loadQueue = (page = pageIndex, size = pageSize, status = statusFilter) => {
    const params = {
      start: page,
      limit: size,
    };

    if (status) {
      params.status = status;
    }

    dispatch(fetchWhatsAppQueue(params));
  };

  useEffect(() => {
    const params = {
      start: pageIndex,
      limit: pageSize,
    };

    if (statusFilter) {
      params.status = statusFilter;
    }

    dispatch(fetchWhatsAppQueue(params));
  }, [dispatch, pageIndex, pageSize, statusFilter]);

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filteredQueueData = useMemo(() => {
    if (!normalizedSearch) {
      return queueData;
    }

    return queueData.filter((item) =>
      [
        item?.recipient,
        item?.templateName,
        item?.messageType,
        item?.wamid,
        item?.errorCode,
        item?.errorMessage,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedSearch))
    );
  }, [queueData, normalizedSearch]);

  const columns = [
    {
      title: "Recipient",
      dataIndex: "recipient",
      width: 150,
    },
    {
      title: "Type",
      dataIndex: "messageType",
      width: 110,
    },
    {
      title: "Template",
      dataIndex: "templateName",
      width: 190,
      render: (value) => value || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 130,
      render: (value) => <Tag color={statusColor(value)}>{value || "-"}</Tag>,
    },
    {
      title: "Retries",
      width: 110,
      render: (_, record) => `${record.retryCount ?? 0}/${record.maxRetries ?? 0}`,
    },
    {
      title: "WAMID",
      dataIndex: "wamid",
      width: 190,
      render: (value) => (
        <Tooltip title={value}>
          <span>{truncate(value, 32)}</span>
        </Tooltip>
      ),
    },
    {
      title: "Next Retry",
      dataIndex: "nextRetryAt",
      width: 180,
      render: formatWhatsAppDate,
    },
    {
      title: "Sent",
      dataIndex: "sentAt",
      width: 180,
      render: formatWhatsAppDate,
    },
    {
      title: "Delivered",
      dataIndex: "deliveredAt",
      width: 180,
      render: formatWhatsAppDate,
    },
    {
      title: "Read",
      dataIndex: "readAt",
      width: 180,
      render: formatWhatsAppDate,
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      width: 180,
      render: formatWhatsAppDate,
    },
    {
      title: "Error",
      dataIndex: "errorMessage",
      width: 260,
      render: (value, record) => (
        <Tooltip title={value}>
          <span>{record.errorCode ? `${record.errorCode}: ` : ""}{truncate(value)}</span>
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="w-full h-full whatsapp-page weiser-soft-scrollbar">
      <InsideHeader
        title="WhatsApp Message Queue"
        subtitle="Monitor outbound WhatsApp queue statuses"
        back={true}
        compact={true}
      />

      <div className="whatsapp-table-shell">
        <div className="whatsapp-toolbar whatsapp-toolbar-compact">
          <Space wrap className="whatsapp-toolbar-filters">
            <Select
              allowClear
              placeholder="Status"
              value={statusFilter}
              onChange={(value) => {
                setPageIndex(0);
                setStatusFilter(value);
              }}
              options={statusOptions}
              className="whatsapp-filter-control"
            />
            <Input
              placeholder="Search recipient, type, template or WAMID"
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(event) => {
                setPageIndex(0);
                setSearchTerm(event.target.value);
              }}
              className="whatsapp-filter-control whatsapp-filter-control-wide"
            />
          </Space>

          <Space wrap>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => loadQueue(pageIndex, pageSize, statusFilter)}
              loading={loadingQueue}
            >
              Refresh
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={filteredQueueData}
          loading={loadingQueue}
          rowKey={(record) => record.id}
          scroll={{ x: 2100 }}
          locale={{ emptyText: <Empty description="No WhatsApp queue messages found" /> }}
          pagination={{
            position: ["bottomCenter"],
            current: pageIndex + 1,
            total: normalizedSearch ? filteredQueueData.length : queueCount,
            pageSize,
            showSizeChanger: false,
            hideOnSinglePage: true,
            onChange: (page, size) => {
              setPageIndex(page - 1);
              setPageSize(size);
            },
          }}
        />
      </div>
    </div>
  );
};

export default WhatsAppQueue;
